/**
 * O cronograma mais claro — a navegação (onda 378, spec 15 §3.5, D4).
 *
 * «Clique da rodinha + arrastar move a tela; zoom nos dias por − / régua / +,
 *  Ctrl + rodinha, pinça e teclas + −» (Humberto, 03/11/2026).
 *
 * Funções PURAS e sem importação: o teste 362 as transforma pelo esbuild do
 * próprio web e prova sem navegador, como o 357 faz com a regra do ensaio.
 *
 * Unidades: `larguraDia` é a largura de um dia em px; `xCursor` é a posição do
 * ponteiro em px medida do começo VISÍVEL da linha do tempo (sem a coluna dos
 * nomes); `rolagem` é o `scrollLeft` da grade. O dia sob o cursor é
 * `(xCursor + rolagem) / larguraDia` — e é ele que o zoom mantém parado.
 */

/** A menor largura do dia: ~6 meses numa tela de 1366 px. */
export const LARGURA_MINIMA = 4;
/** A maior: ~6 dias numa tela de 1366 px. */
export const LARGURA_MAXIMA = 120;

/** A largura do dia dentro de [4, 120] px. */
export const limitarLargura = (larguraDia: number): number =>
  Math.min(LARGURA_MAXIMA, Math.max(LARGURA_MINIMA, larguraDia));

/**
 * O zoom que fixa o dia sob o cursor: a largura nova é `larguraDia × fator`
 * (limitada a [4, 120]) e a rolagem nova põe o mesmo dia sob o mesmo ponto.
 * A rolagem não fica negativa (o começo da janela é o limite).
 */
export function zoomNoCursor(
  larguraDia: number, fator: number, xCursor: number, rolagem: number,
): { larguraDia: number; rolagem: number } {
  const nova = limitarLargura(larguraDia * fator);
  const dia = (xCursor + rolagem) / larguraDia;
  return { larguraDia: nova, rolagem: Math.max(0, dia * nova - xCursor) };
}

/** O cabeçalho troca dias → semanas → meses conforme o zoom (≥ 18 dia; ≥ 6 semana; senão mês). */
export function escalaDoCabecalho(larguraDia: number): 'dia' | 'semana' | 'mes' {
  if (larguraDia >= 18) return 'dia';
  if (larguraDia >= 6) return 'semana';
  return 'mes';
}

/** Os atalhos Dia · Semana · Mês: a largura do dia em cada um (cada um cai na sua escala). */
export const ATALHO_DA_ESCALA: Record<'dia' | 'semana' | 'mes', number> = {
  dia: 30, semana: 10, mes: 4,
};

/** O passo dos botões − + e das teclas − +. */
export const PASSO_DO_ZOOM = 1.25;

/**
 * Quantos dias a tela pede ao servidor: a janela escolhida («Dias»), ou mais,
 * quando o zoom mostra mais dias do que ela tem — em degraus de 30 para não
 * recarregar a cada giro da rodinha, até os 180 que o servidor aceita.
 */
export function janelaDoZoom(escolhida: number, larguraVisivel: number, larguraDia: number): number {
  const visiveis = Math.ceil(Math.max(0, larguraVisivel) / larguraDia) + 7;
  if (visiveis <= escolhida) return escolhida;
  return Math.min(180, Math.ceil(visiveis / 30) * 30);
}

/**
 * «Ajustar»: enquadra os dias [primeiro, último] (contados em dias) na largura
 * visível, com um dia de folga de cada lado. Devolve o primeiro dia da janela e
 * a largura do dia.
 */
export function ajustar(primeiro: number, ultimo: number, larguraVisivel: number): { de: number; larguraDia: number } {
  const de = Math.min(primeiro, ultimo) - 1;
  const dias = Math.abs(ultimo - primeiro) + 3;
  return { de, larguraDia: limitarLargura(Math.floor((larguraVisivel / dias) * 100) / 100) };
}

/* ── O que o aparelho lembra (spec 15 §3.2) ─────────────────────────── */

/** A chave do armazenamento local — por aparelho, não por pessoa. */
export const CHAVE_DA_PREFERENCIA = 'cronograma.v1';

export interface PreferenciaDoCronograma {
  bandejaAberta: boolean;
  larguraDia: number;
  ocultarVazias: boolean;
}

export const PREFERENCIA_PADRAO: PreferenciaDoCronograma = {
  bandejaAberta: true, larguraDia: ATALHO_DA_ESCALA.dia, ocultarVazias: false,
};

/** Lê o que o aparelho guardou; sem armazenamento, ou com lixo, o padrão. */
export function lerPreferencia(armazem: { getItem(k: string): string | null } | null | undefined): PreferenciaDoCronograma {
  try {
    const bruto = armazem?.getItem(CHAVE_DA_PREFERENCIA);
    if (!bruto) return { ...PREFERENCIA_PADRAO };
    const p = JSON.parse(bruto) as Partial<PreferenciaDoCronograma>;
    return {
      bandejaAberta: typeof p.bandejaAberta === 'boolean' ? p.bandejaAberta : PREFERENCIA_PADRAO.bandejaAberta,
      larguraDia: typeof p.larguraDia === 'number' && Number.isFinite(p.larguraDia)
        ? limitarLargura(p.larguraDia) : PREFERENCIA_PADRAO.larguraDia,
      ocultarVazias: typeof p.ocultarVazias === 'boolean' ? p.ocultarVazias : PREFERENCIA_PADRAO.ocultarVazias,
    };
  } catch {
    return { ...PREFERENCIA_PADRAO };
  }
}

/** Grava sem quebrar a tela quando o aparelho não deixa (aba anônima, cota cheia). */
export function gravarPreferencia(
  armazem: { setItem(k: string, v: string): void } | null | undefined, p: PreferenciaDoCronograma,
): void {
  try { armazem?.setItem(CHAVE_DA_PREFERENCIA, JSON.stringify(p)); } catch { /* o aparelho não guarda */ }
}
